import { RolodexDocumentEntry } from 'features/files'
import { useDataHealthMetrics } from 'features/libra/stepComponents/niiDataHealth/useDataHealthMetrics'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'

import { useLibraDocument } from './LibraDocumentContext'

/**
 * Context for document data management - handles fetching, caching, and refreshing
 * Follows single responsibility principle by only managing data, not UI state
 */
interface DocumentDataContextType {
  // Document data
  metricsByCategory: ReadonlyMap<string, number>
  documentsByCategory: ReadonlyMap<string, ReadonlyArray<RolodexDocumentEntry>>
  totalDocuments: number
  isLoading: boolean
  forceRefresh: () => Promise<void>

  // Configuration (read from workflow global context)
  requiredCategories: ReadonlyArray<string>
}

const DocumentDataContext = createContext<DocumentDataContextType | undefined>(
  undefined
)

interface DocumentDataProviderProps {
  unitId: string | undefined
  children: ReactNode
}

export const DocumentDataProvider: FC<DocumentDataProviderProps> = ({
  unitId,
  children,
}) => {
  const workflow = useWorkflow()
  const { selectedDocumentId, setSelectedDocumentId } = useLibraDocument()

  // Read required categories from workflow global context
  const requiredCategories = useMemo(() => {
    const documentConfig = workflow.globalContext.documentConfig
    if (
      documentConfig &&
      typeof documentConfig === 'object' &&
      !Array.isArray(documentConfig)
    ) {
      const categories = (documentConfig as Record<string, unknown>)
        .requiredCategories
      if (Array.isArray(categories)) {
        return categories.filter(
          (cat): cat is string => typeof cat === 'string'
        )
      }
    }
    return []
  }, [workflow.globalContext.documentConfig])

  const {
    metricsByCategory,
    documentsByCategory,
    totalDocuments,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useDataHealthMetrics(unitId)

  if (!selectedDocumentId && hasEverLoaded && totalDocuments > 0) {
    const firstDocument = Array.from(documentsByCategory.values()).find(
      (docs) => docs.length > 0
    )?.[0]

    if (firstDocument) {
      setSelectedDocumentId(firstDocument.entry_id)
    }
  }

  const contextValue = useMemo<DocumentDataContextType>(
    () => ({
      metricsByCategory,
      documentsByCategory,
      totalDocuments,
      isLoading,
      forceRefresh,
      requiredCategories,
    }),
    [
      metricsByCategory,
      documentsByCategory,
      totalDocuments,
      isLoading,
      forceRefresh,
      requiredCategories,
    ]
  )

  return (
    <DocumentDataContext.Provider value={contextValue}>
      {children}
    </DocumentDataContext.Provider>
  )
}

export const useDocumentData = (): DocumentDataContextType => {
  const context = useContext(DocumentDataContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentData must be used within a DocumentDataProvider'
    )
  }
  return context
}
